import React, { useState } from "react";
import globalAxios from "axios";
import { toast } from "react-toastify";
import { FiCloudOff } from "react-icons/fi";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import GatedButton from "./GatedButton";
import { lockMessageFrom } from "../Pages/Publication/publicationUtils";
import RemovalImpactModal from "./RemovalImpactModal";
import { RemovalImpact, fetchImpact } from "./Redirects/redirectsApi";

type UnpublishProductionButtonProps = {
	/** The stable content id : POST /api/deployment/unpublish-content { id }. */
	contentId?: string | null;
	/** "compact" renders an icon-only button (list rows); default renders the labelled button. */
	variant?: "default" | "compact";
	disabled?: boolean;
	className?: string;
	/** Button text. Toolbars pass a short « Dépublier » : the confirm modal names the target anyway. */
	label?: React.ReactNode;
	/** Called after a successful unpublish so the caller can refresh its status data. */
	onUnpublished?: () => void;
	onWorkingChange?: (working: boolean) => void;
};

/**
 * « Dépublier de la production » : removes THIS content from the production site through the admin
 * pipeline while keeping the admin source (and preview) intact, so it can be re-published later.
 * Before anything, GET /api/redirects/impact is consulted : if the page is still referenced (static
 * inbound links or redirects targeting its URL), the impact modal opens FIRST so the editor can fix
 * the links, create a redirect, or knowingly proceed. No impact (or impact check failure : fail-open)
 * keeps the plain confirmation modal. Errors stay in the modal (409 = a publication lock is held,
 * 404 = gone).
 */
const UnpublishProductionButton: React.FC<UnpublishProductionButtonProps> = ({
	contentId,
	variant = "default",
	disabled,
	className,
	label = "Dépublier production",
	onUnpublished,
	onWorkingChange
}) => {
	const [showConfirm, setShowConfirm] = useState(false);
	const [showImpact, setShowImpact] = useState(false);
	const [impact, setImpact] = useState<RemovalImpact | null>(null);
	const [checkingImpact, setCheckingImpact] = useState(false);
	const [working, setWorking] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Deploying/unpublishing production is a reserved right: authors write content, but only holders of
	// the deployment permission can change what is live. Sans elle, le bouton reste visible mais
	// grisé-cliquable (GatedButton) et le clic explique la permission manquante (the server also
	// enforces it with a 403).

	const setBusy = (busy: boolean) => {
		setWorking(busy);
		onWorkingChange?.(busy);
	};

	const unpublish = async () => {
		if (!contentId) return;
		setBusy(true);
		setError(null);
		try {
			await globalAxios.post(
				`/api/deployment/unpublish-content`,
				{ id: contentId },
				{ withCredentials: true, headers: { Accept: "application/json" } }
			);
			setShowConfirm(false);
			setShowImpact(false);
			toast.success("Retiré de la production ✓");
			onUnpublished?.();
		} catch (err: any) {
			console.error(err);
			const status = err?.response?.status;
			if (status === 409) setError(lockMessageFrom(err));
			else if (status === 404) setError("Contenu introuvable.");
			else setError("La dépublication a échoué. Réessayez ou consultez les journaux.");
		} finally {
			setBusy(false);
		}
	};

	const openConfirm = async (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!contentId || checkingImpact) return;
		setError(null);
		setCheckingImpact(true);
		try {
			// Règle arbitrée : seuls de VRAIS liens statiques entrants ouvrent la modale d'impact.
			// Une redirection qui vise l'URL ne suffit plus (le 404 est assumé, la règle reste
			// visible dans redirect-health). Sinon (ou si l'évaluation échoue, fail-open),
			// confirmation classique.
			const evaluated = await fetchImpact(contentId);
			if (evaluated.staticLinks.length > 0) {
				setImpact(evaluated);
				setShowImpact(true);
			} else {
				setShowConfirm(true);
			}
		} catch (err) {
			console.error("Échec de l'évaluation d'impact : on retombe sur la confirmation simple", err);
			setShowConfirm(true);
		} finally {
			setCheckingImpact(false);
		}
	};

	const isDisabled = disabled || !contentId || checkingImpact;

	return (
		<>
			{variant === "compact" ? (
				<GatedButton
					token="feature:deployment:write"
					onClick={openConfirm}
					disabled={isDisabled}
					title="Dépublier de la production"
					aria-label="Dépublier de la production"
					className={
						className ??
						"inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-300 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
					}
				>
					{checkingImpact ? <LoadingSpinner size="xs" inline /> : <FiCloudOff className="h-3.5 w-3.5" />}
				</GatedButton>
			) : (
				<GatedButton
					token="feature:deployment:write"
					onClick={openConfirm}
					disabled={isDisabled}
					className={
						className ??
						"inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-600 shadow-sm transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
					}
				>
					{checkingImpact ? <LoadingSpinner size="sm" inline /> : <FiCloudOff className="shrink-0" />} {label}
				</GatedButton>
			)}

			{/* Chemin sans impact : la confirmation classique, inchangée. */}
			<Modal show={showConfirm} onHide={() => (working ? undefined : setShowConfirm(false))} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Dépublier de la production ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Le contenu sera retiré du site de production. La source reste intacte : vous pourrez le republier à tout moment.
					</p>
					{error && (
						<p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
							{error}
						</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirm(false)}
						disabled={working}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={unpublish}
						disabled={working}
						className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{working ? <LoadingSpinner size="sm" inline /> : <FiCloudOff className="h-4 w-4" />}
						{working ? "Dépublication…" : "Dépublier"}
					</button>
				</Modal.Footer>
			</Modal>

			{/* Chemin avec impact : la page est encore référencée : corriger / rediriger / forcer. */}
			{contentId && (
				<RemovalImpactModal
					show={showImpact}
					contentId={contentId}
					impact={impact}
					actionLabel="Dépublier"
					busy={working}
					error={error}
					onProceed={unpublish}
					onCancel={() => setShowImpact(false)}
				/>
			)}
		</>
	);
};

export default UnpublishProductionButton;
